import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from '@headlessui/react'
import {
  Cog6ToothIcon,
  HomeIcon,
  UsersIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  ScaleIcon,
  TrophyIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import FirmTopBar from './FirmTopBar'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: "Today's ToDos", href: '/todays-todos', icon: CheckCircleIcon },
  { name: '341 Workboard', href: '/341-workboard', icon: CalendarDaysIcon },
  { name: 'Reaffirmation Workboard', href: '/reaffirmation-workboard', icon: DocumentDuplicateIcon },
  { name: 'Workout Planner', href: '/workouts', icon: TrophyIcon },
  { name: 'Case Search', href: '/case-search', icon: MagnifyingGlassIcon },
  { name: 'Hearings', href: '/firm-hearings', icon: HomeIcon },
  { name: '341 / Hearing Report', href: '/reports/case-date', icon: DocumentDuplicateIcon, current: false },
  { name: 'Prospect Lawsuit Report', href: '/reports/prospect-lawsuits', icon: ScaleIcon, current: false },
  { name: 'Account Management', href: '/account', icon: UsersIcon, current: false },
  { name: 'Add Attorney', href: '/add-attorney', icon: FolderIcon, current: false },
  { name: 'Leads Center', href: '/leads', icon: FolderIcon, current: false },
  { name: 'Evaluation Center', href: '/lead-center', icon: FolderIcon, current: false },
  { name: 'Chapter 13 Lead Center', href: '/chapter-13-lead-center', icon: FolderIcon, current: false },
  { name: 'Archived Cases', href: '/archived', icon: FolderIcon, current: false },
  { name: 'Create Case', href: '/create-case', icon: DocumentDuplicateIcon, current: false },
  { name: 'Templates', href: '/trustee-templates', icon: DocumentDuplicateIcon, current: false},
]

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function DashboardShell({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const location = useLocation();
  const navItems = navigation.map(item => ({
    ...item,
    current: location.pathname === item.href
  }));

  return (
    <div>
      {/* Mobile Sidebar */}
      <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-50 lg:hidden">
        <DialogBackdrop className="fixed inset-0 bg-gray-900/80 transition-opacity" />
        <div className="fixed inset-0 flex">
          <DialogPanel className="relative mr-16 flex w-full max-w-xs flex-1 transform transition">
            <TransitionChild>
              <div className="absolute top-0 left-full flex w-16 justify-center pt-5">
                <button onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                  <XMarkIcon className="w-6 h-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
              <div className="flex h-16 shrink-0 items-center">
                <h2 className="text-lg font-bold">Firm Dashboard</h2>
              </div>
              <nav className="flex flex-1 flex-col">
                <ul className="-mx-2 space-y-1">
                  {navItems.map((item) => (
                    <li key={item.name}>
                      <a
                        href={item.href}
                        className={classNames(
                          item.current ? 'bg-indigo-100 text-indigo-700' : 'text-gray-700 hover:bg-gray-50 hover:text-indigo-600',
                          'group flex gap-x-3 rounded-md p-2 text-sm font-semibold'
                        )}
                      >
                        <item.icon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600" />
                        {item.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 bg-slate-50 lg:flex lg:w-72 lg:flex-col border-r border-gray-200 bg-white px-6 pb-4">
        <div className="flex h-16 shrink-0 items-center">
          <h2 className="text-xl font-bold">Title 11 U.S.C Relief</h2>
        </div>
        <nav className="flex flex-1 flex-col mt-4">
          <ul className="flex flex-1 flex-col gap-y-7">
            <li>
              <ul className="-mx-2 space-y-1">
                {navItems.map((item) => (
                  <li key={item.name}>
                    <a
                      href={item.href}
                      className={classNames(
                        item.current ? 'bg-blue-100 text-indigo-700' : 'text-gray-700 hover:bg-blue-50 hover:text-indigo-600',
                        'group flex gap-x-3 rounded-md p-2 text-sm font-semibold'
                      )}
                    >
                      <item.icon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600" />
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </li>
            <li className="mt-auto">
              <a href="/account" className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:text-indigo-600">
                <Cog6ToothIcon className="w-6 h-6 text-gray-400 group-hover:text-indigo-600" />
                Settings
              </a>
            </li>
          </ul>
        </nav>
      </div>

      {/* Top bar and main content */}
      <div className="lg:pl-72">
        <FirmTopBar onOpenSidebar={() => setSidebarOpen(true)} />

        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
